/* Warm a lazily loaded image before it reaches the screen.

   Native lazy loading decides for itself how far ahead to fetch, and on a slow
   connection it fetches later, not earlier, which is exactly when the delay is
   most visible: the frame sits empty while you scroll onto it and the picture
   drops in afterwards.

   This watches every lazy image and, about two screens before it arrives,
   fetches the candidate the browser is going to want into the HTTP cache. The
   image element is left alone. That matters: the theme's lazy images carry
   sizes="auto", which is only valid while loading="lazy", so flipping them to
   eager would silently send the request back to a full viewport width and undo
   the size they are fetched at.

   The candidate is chosen the way the browser chooses one for a w descriptor
   set: the narrowest that still covers the element's painted width times the
   device pixel ratio. Picking the same one is what makes this a warm cache hit
   rather than a second download. */
(function () {
  if (!('IntersectionObserver' in window)) return;

  var AHEAD = '1600px 0px';
  var warmed = new WeakSet ? new WeakSet() : null;

  function candidate(img) {
    var srcset = img.getAttribute('srcset');
    var src = img.getAttribute('src');
    if (!srcset) return src;

    /* An image in a hidden slide has no painted width, so there is no way to
       tell which candidate the browser will want. Guessing would mean warming
       the full width default, which on a slow connection is the opposite of
       the point. It is left for the browser. */
    var want = Math.ceil(img.getBoundingClientRect().width * (window.devicePixelRatio || 1));
    if (!want) return null;

    var best = null;
    var bestWidth = Infinity;
    var largest = null;
    var largestWidth = 0;

    srcset.split(',').forEach(function (part) {
      var bits = part.trim().split(/\s+/);
      if (bits.length < 2) return;
      var width = parseInt(bits[1], 10);
      if (!width) return;
      if (width > largestWidth) {
        largestWidth = width;
        largest = bits[0];
      }
      if (width >= want && width < bestWidth) {
        bestWidth = width;
        best = bits[0];
      }
    });

    return best || largest || src;
  }

  function warm(img) {
    if (warmed && warmed.has(img)) return;
    if (warmed) warmed.add(img);
    if (img.complete && img.naturalWidth) return;

    var url = candidate(img);
    if (!url) return;

    var pre = new Image();
    pre.decoding = 'async';
    if ('fetchPriority' in pre) pre.fetchPriority = 'low';
    pre.src = url;
  }

  var observer = new IntersectionObserver(
    function (entries) {
      for (var i = 0; i < entries.length; i++) {
        if (!entries[i].isIntersecting) continue;
        observer.unobserve(entries[i].target);
        warm(entries[i].target);
      }
    },
    { rootMargin: AHEAD }
  );

  var queued = false;

  function scan() {
    queued = false;
    var imgs = document.querySelectorAll('img[loading="lazy"]');
    for (var i = 0; i < imgs.length; i++) {
      if (warmed && warmed.has(imgs[i])) continue;
      observer.observe(imgs[i]);
    }
  }

  function schedule() {
    if (queued) return;
    queued = true;
    requestAnimationFrame(scan);
  }

  schedule();
  document.addEventListener('DOMContentLoaded', schedule);
  window.addEventListener('load', schedule);

  if (window.MutationObserver) {
    new MutationObserver(schedule).observe(document.documentElement, {
      childList: true,
      subtree: true,
    });
  }
})();
